/**
 * Shared alert-control primitives (#234).
 *
 * The canonical Settings panel (#232) and the compact Position Tracker pull-up
 * (#234) are two views onto ONE preference store, so their controls must be one
 * implementation — two hand-rolled switches would drift in styling and, worse,
 * in the accessible-name convention the Settings tests query by.
 *
 * Presentational only: no hook, no storage, no registry lookup. Every label a
 * caller passes in still comes from `ALERT_REGISTRY`.
 *
 * `atoms/Toggle` is deliberately NOT reused here: it renders no `aria-label`
 * (its accessible name comes from visible label text only), so adopting it
 * would mean changing Settings' DOM and its pinned control names for no gain.
 */

import { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import type {
  AlertCategory,
  AlertChannelId,
  AlertChannelSettings,
} from '@/lib/alerts/registry';
import {
  clampAgainstSibling,
  type DeliveryThresholdMeta,
  type DeliveryThresholdUnit,
} from '@/lib/alerts/deliveryThresholds';

/**
 * Display names for a category enum the registry defines but does not name.
 * A total `Record`, so a new `AlertCategory` fails the build here rather than
 * rendering an unlabelled group. This is a label lookup, not a second registry
 * of channel data — every per-channel string still comes from `ALERT_REGISTRY`.
 */
export const CATEGORY_LABELS: Record<AlertCategory, string> = {
  fills: 'Fills',
  orders: 'Orders',
  queue: 'Queue',
  divergence: 'Cross-venue',
  system: 'System',
  risk: 'Risk',
};

/**
 * Unit suffix for a threshold field. Total for the same reason as the category
 * labels: a new unit must fail the build, not render a bare number.
 */
export const THRESHOLD_UNIT_SUFFIX: Record<DeliveryThresholdUnit, string> = {
  contracts: 'contracts',
  contractsPerSecond: '/s',
  cents: '¢',
};

/**
 * Which side of a channel a switch controls.
 *
 * A union rather than a free-form `name` string so the accessible-name
 * convention ("<label> sound" / "<label> flash") cannot drift between the two
 * surfaces — `AlertSettingsPanel.test.tsx` queries switches by that exact name.
 */
export type AlertSwitchKind = 'sound' | 'flash';

export function AlertSwitch({
  checked,
  label,
  kind,
  onChange,
}: {
  checked: boolean;
  /** The channel's registry label. */
  label: string;
  kind: AlertSwitchKind;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`${label} ${kind}`}
      onClick={() => onChange(!checked)}
      className={`focus:ring-primary/50 focus:ring-offset-background relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none ${
        checked ? 'bg-blue-600' : 'bg-muted-foreground/30'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4' : ''
        }`}
      />
    </button>
  );
}

/**
 * Number field for one channel's delivery threshold.
 *
 * COMMIT SEMANTICS. Typing never writes. The value reaches the store on blur,
 * on Enter, on a stepper click, or when the field unmounts with an unsaved
 * edit — because `setChannelDeliveryThreshold` persists every call and clamps
 * it against the channel's ordered sibling. A per-keystroke write of "150000"
 * would persist 1, then 15, then 150, each one clamped, permanently moving a
 * level the trader never chose.
 *
 * `draft` is non-null ONLY after a real keystroke, and that is load-bearing in
 * three places:
 *
 *  - Focus alone seeds nothing, so tabbing through a field cannot write. An
 *    earlier version seeded on focus, which meant an untouched blur re-committed
 *    the focus-time value — silently reverting a concurrent cross-tab edit.
 *  - Escape only consumes itself while an edit is live; with nothing to abandon
 *    it belongs to the popover, which is where a second Escape should go.
 *  - The unmount flush fires only for a genuine pending edit. That is what makes
 *    "type a level, then click outside to dismiss" SAVE rather than silently
 *    discard: the outside-click handler unmounts this field before the browser
 *    moves focus, so `blur` never arrives. It also makes dismissing agree with
 *    the close button, which does blur first and therefore always committed.
 *
 * CLAMP CONTRACT. `onCommit` may persist a DIFFERENT number than it was passed
 * (registry bounds, plus one step clear of the ordered sibling). `value` is
 * therefore always the persisted value from `prefs`, and the field re-renders
 * from it — the visible snap to the stored number is the user's feedback, and
 * it is truthful.
 */
export function AlertThresholdField({
  id,
  label,
  value,
  meta,
  channels,
  onCommit,
}: {
  id: AlertChannelId;
  /** The channel's registry label — carries the accessible names. */
  label: string;
  /** The PERSISTED threshold. Never a local mirror, never a default. */
  value: number;
  meta: DeliveryThresholdMeta;
  /** The whole map — the steppers ask the store what a step WOULD persist. */
  channels: Readonly<Record<AlertChannelId, AlertChannelSettings>>;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? String(value);

  const commit = (raw: string) => {
    setDraft(null);
    const next = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(next)) return;
    onCommit(next);
  };

  // Flush a pending edit on unmount. Refs, because the cleanup must see the
  // LATEST draft and callback rather than the ones captured at mount.
  const pending = useRef({ draft, commit });
  useEffect(() => {
    pending.current = { draft, commit };
  });
  useEffect(
    () => () => {
      const { draft: last, commit: flush } = pending.current;
      if (last !== null) flush(last);
    },
    []
  );

  // A step is offered only when it would actually move the stored value.
  // `clampAgainstSibling` is the store's OWN function, so the button asks the
  // single source of truth instead of re-deriving pair rules in the UI. At a
  // pair boundary a step is a silent no-op, and a button that looks live but
  // does nothing is the dead control this surface exists to avoid.
  const wouldMove = (next: number) => clampAgainstSibling(id, next, channels) !== value;

  return (
    <div className="flex items-center gap-1.5">
      <label htmlFor={`alert-threshold-${id}`} className="text-muted-foreground shrink-0 text-[11px]">
        Level
      </label>
      <button
        type="button"
        aria-label={`Decrease ${label} level`}
        onClick={() => onCommit(value - meta.step)}
        disabled={value <= meta.min || !wouldMove(value - meta.step)}
        className="bg-muted hover:bg-muted-foreground/20 focus:ring-primary/50 flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors focus:ring-2 focus:outline-none disabled:opacity-40"
      >
        <Minus size={11} aria-hidden="true" />
      </button>
      <input
        id={`alert-threshold-${id}`}
        type="number"
        inputMode="decimal"
        min={meta.min}
        max={meta.max}
        step={meta.step}
        value={shown}
        // The visible label reads "Level" on every row; the accessible name
        // carries the channel so a screen-reader user can tell them apart.
        aria-label={`${label} level`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          if (draft !== null) commit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            // Guarded so Enter on an untouched field is inert, and so the blur
            // that follows a committing Enter cannot write the same value twice.
            if (draft !== null) commit(e.currentTarget.value);
            return;
          }
          if (e.key === 'Escape' && draft !== null) {
            e.stopPropagation();
            setDraft(null);
          }
        }}
        className="bg-card border-border text-foreground w-20 rounded-md border px-2 py-0.5 text-[11px] tabular-nums [appearance:textfield] focus:border-blue-500 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button
        type="button"
        aria-label={`Increase ${label} level`}
        onClick={() => onCommit(value + meta.step)}
        disabled={value >= meta.max || !wouldMove(value + meta.step)}
        className="bg-muted hover:bg-muted-foreground/20 focus:ring-primary/50 flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors focus:ring-2 focus:outline-none disabled:opacity-40"
      >
        <Plus size={11} aria-hidden="true" />
      </button>
      <span className="text-muted-foreground/70 shrink-0 text-[10px]">
        {THRESHOLD_UNIT_SUFFIX[meta.unit]}
      </span>
    </div>
  );
}
